import { useState } from "react";
import { Container, Row, Col, Table, Button, Spinner, Badge, Modal, ListGroup } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faEdit, faTrash, faCircleInfo, faChartLine, faStop } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API_BASE_URL =  process.env.REACT_APP_API_BASE_URL;

const MeasurementRunTable = ({ measurements, loading, onEdit, onDelete, onAddNewMeasurement, auth }) => {
    const navigate = useNavigate();
    const [showDetails, setShowDetails] = useState(false);
    const [selectedMeasurement, setSelectedMeasurement] = useState(null);

    // Navigation handlers
    const handleViewMeasurement = (measurement) => {
        navigate(`/measurement/${measurement.id}`);
    };

    const handleShowGraph = (measurement) => {
        navigate(`/measurement/${measurement.id}/graph`);
    };

    const handleShowDetails = (measurement) => {
        setSelectedMeasurement(measurement);
        setShowDetails(true);
    };

    const handleCloseDetails = () => setShowDetails(false);

    const handleFinishMeasurement = async (measurement) => {
        const headers = { Authorization: `Bearer ${auth.token}` }; // authorization header
        await axios.put(`${API_BASE_URL}/measurements/${measurement.id}`, { status: 'completed' }, { headers });
    }

    if (loading) {
        return (
            <div className="text-center my-5">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">
                        Loading measurements...
                    </span>
                </Spinner>
            </div>
        );
    }

    if (!measurements || measurements.length === 0) {
        return (
            <Container fluid>
                <Row className="mb-3">
                    <Col md={12} className="text-md-end">
                        <Button variant="primary" onClick={onAddNewMeasurement} className="mt-5">
                            <FontAwesomeIcon icon={faPlus} className="me-1" />{" "} Add New
                        </Button>
                    </Col>
                </Row>
                <div className="alert alert-info">No measurement runs found</div>
            </Container>
        );
    }

    return (
        <>
            <Container fluid className="py-4">
                <Row className="mb-3">
                    <Col md={12} className="text-md-end">
                        <Button variant="primary" onClick={onAddNewMeasurement} className="mt-5">
                            <FontAwesomeIcon icon={faPlus} className="me-1" />{" "} Add New
                        </Button>
                    </Col>
                </Row>
                <h2 className="mb-4 fs-2">Measurements</h2>

                <Table striped bordered hover responsive className="mb-0">
                    <thead className="table-striped">
                        <tr>
                            <th>#</th>
                            <th>Device ID</th>
                            <th>Stations</th>
                            <th>User Path</th>
                            <th>Measurement Type</th>
                            <th>Status</th>
                            <th>Sensors</th>
                            <th>Description</th>
                            <th>SSID</th>
                            <th>IP</th>
                            <th>Created</th>
                            <th>Updated</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {measurements.map((measurement, index) => (
                            <tr key={measurement.id || index}>
                                <td>{index + 1}</td>
                                <td>{measurement.device_identifier}</td>
                                <td>
                                    <div className="d-flex gap-1">
                                        {measurement.stations?.map(
                                            (station) => (
                                                <Badge key={station} bg="secondary"> Station {station} </Badge>
                                            )
                                        )}
                                    </div>
                                </td>
                                <td>{measurement.user}</td>
                                <td>{measurement.measurement_type || "-"}</td>
                                <td>{measurement.status}</td>
                                <td>
                                    <div className="sensor-cell">
                                        {measurement.sensors_with_channels?.length ? (
                                            <>
                                                <div className="sensor-badges mb-2">
                                                    {measurement.sensors_with_channels
                                                        .slice(0, 3)
                                                        .map((sc, idx) => (
                                                            <Badge key={idx} bg="light" text="dark" className="me-1 mb-1 sensor-badge fs-6">
                                                                <small>
                                                                    <strong> { sc.serial_number } </strong>
                                                                    :{" "}
                                                                    {sc.channel}
                                                                </small>
                                                            </Badge>
                                                        ))
                                                    }
                                                </div>
                                                <div className="text-start mt-1">
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        onClick={() => handleShowDetails(measurement)}
                                                        className="details-btn"
                                                    >
                                                        {measurement.sensors_with_channels.length > 3 ? (
                                                            <>
                                                                + {measurement.sensors_with_channels.length - 3} more
                                                            </>
                                                            ) : (
                                                            <>
                                                                <FontAwesomeIcon icon={faCircleInfo} className="me-1" />
                                                                More
                                                            </>
                                                        )}
                                                    </Button>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="text-start text-muted py-1">
                                                <small>No sensors assigned</small>
                                            </div>
                                        )}
                                    </div>
                                </td>
                                <td>{measurement.run_description || "-"}</td>
                                <td>{measurement.ssid || "N/A"}</td>
                                <td>{measurement.ip_address || "N/A"}</td>
                                <td>
                                    {new Date(measurement.created_at).toLocaleDateString()}
                                    <br />
                                    <small className="text-muted">
                                        {new Date(measurement.created_at).toLocaleTimeString()}
                                    </small>
                                </td>
                                <td>
                                    {measurement.updated_at ? (
                                        <>
                                            {new Date(measurement.updated_at).toLocaleDateString()}
                                            <br />
                                            <small className="text-muted">
                                                {new Date(measurement.updated_at).toLocaleTimeString()}
                                            </small>
                                        </>
                                    ) : (
                                        "-"
                                    )}
                                </td>
                                <td>
                                    <div className="d-flex gap-2 justify-content-center">
                                        <Button variant="success" size="sm"
                                            onClick={() =>
                                            handleFinishMeasurement(measurement)}
                                            className="text-white"
                                            title="Complete measurement"
                                        >
                                            <FontAwesomeIcon icon={faStop} />
                                        </Button>

                                        <Button variant="warning" size="sm" onClick={() =>
                                            onEdit(measurement)}
                                            className="d-flex align-items-center gap-2"
                                            title="Edit"
                                        >
                                            <FontAwesomeIcon icon={faEdit}/>
                                        </Button>

                                        <Button variant="danger" size="sm"
                                            onClick={() => {
                                                if ( window.confirm("Delete this measurement?")) {
                                                    onDelete(measurement.id);
                                                }
                                            }}
                                            title="Delete"
                                        >
                                            <FontAwesomeIcon icon={faTrash}/>
                                        </Button>

                                        <Button variant="info" size="sm"
                                            onClick={() =>
                                            handleViewMeasurement(measurement)}
                                            className="text-white"
                                            title="Info"
                                        >
                                            <FontAwesomeIcon icon={faCircleInfo}/>
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
                <div className="d-flex justify-content-end mt-3 me-3">
                    {loading && (
                        <Spinner animation="border" size="sm" className="me-2"/>
                    )}
                    <span className="text-muted"> Showing {measurements?.length} records </span>
                </div>
            </Container>

            <Modal show={showDetails} onHide={handleCloseDetails} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        Sensor Details for Measurement
                        <p className="fs-5 mt-1 fst-italic"> {selectedMeasurement?.filename || "View Details"} </p>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedMeasurement?.sensors_with_channels?.length ? (
                        <>
                            <ListGroup variant="flush">
                                {selectedMeasurement.sensors_with_channels.map(
                                    (sc, index) => (
                                        <ListGroup.Item key={index}>
                                            <div className="d-flex justify-content-between">
                                                <div>
                                                    <strong> Sensor:{" "} {sc.serial_number}</strong>
                                                    <div className="text-muted small"> Channel: {sc.channel}</div>
                                                </div>
                                                <div>
                                                    <Badge bg="light" text="dark">Placeholder {index + 1}</Badge>
                                                </div>
                                            </div>
                                        </ListGroup.Item>
                                    )
                                )}
                            </ListGroup>
                            <div className="mt-3 text-end">
                                <small className="text-muted"> Showing {" "} {selectedMeasurement.sensors_with_channels.length}{" "} sensors</small>
                            </div>
                        </>
                    ) : (
                        <div className="text-center py-3 text-muted">No sensors configured for this measurement</div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="success" onClick={() => {
                        handleShowGraph(selectedMeasurement);
                        handleCloseDetails();
                    }}>
                        <FontAwesomeIcon icon={faChartLine} /> See Processed Data Graphs
                    </Button>
                    <Button variant="secondary"
                        onClick={handleCloseDetails}
                    > Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default MeasurementRunTable;